package com.kanati.admin.features.schools

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.shared.school.CreateSchoolRequest
import com.kanati.shared.school.School
import com.kanati.shared.school.UpdateSchoolRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private data class EditSchoolDraft(
    val schoolId: String,
    val name: String,
    val shortName: String,
    val active: Boolean,
)

@Composable
fun SchoolsScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
    onSchoolsChanged: (List<School>) -> Unit,
    onSchoolSaved: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var schools by remember { mutableStateOf<List<School>>(emptyList()) }
    var isRefreshing by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var formError by remember { mutableStateOf<String?>(null) }
    var formSuccess by remember { mutableStateOf<String?>(null) }
    var draftName by remember { mutableStateOf("") }
    var draftShortName by remember { mutableStateOf("") }
    var draftActive by remember { mutableStateOf(true) }
    var editDraft by remember { mutableStateOf<EditSchoolDraft?>(null) }

    fun missingDistrictMessage(): String = when {
        districtLoading -> "Loading district context..."
        districtLoadError != null -> "District context could not be loaded. Refresh context and select a district."
        else -> "Select a district to manage schools."
    }

    suspend fun refreshSchools(selectedDistrict: SelectedDistrictContext) {
        isRefreshing = true
        loadError = null
        try {
            schools = apiClient.getSchools(selectedDistrict.id).sortedBy { it.name.lowercase() }
            onSchoolsChanged(schools)
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load schools."
        } finally {
            isRefreshing = false
        }
    }

    LaunchedEffect(district?.id, districtLoading, districtLoadError) {
        val selectedDistrict = district
        formError = null
        formSuccess = null
        editDraft = null
        if (selectedDistrict != null) {
            refreshSchools(selectedDistrict)
        } else {
            schools = emptyList()
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Schools",
        theme = theme,
        description = "Districts own schools. Operational work happens inside the selected school, so this is where district admins keep the school list honest.",
        headerActions = {
            ActionButton(
                label = if (isRefreshing) "Loading..." else "Refresh",
                onClick = {
                    val selectedDistrict = district ?: return@ActionButton
                    scope.launch { refreshSchools(selectedDistrict) }
                },
                theme = theme,
                isPrimary = false,
                isDisabled = isRefreshing || district == null,
            )
        },
    ) {
        if (district == null) {
            StatusBanner(
                message = missingDistrictMessage(),
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
            return@SectionCard
        }

        if (!canEdit) {
            StatusBanner(
                message = "Read-only access. School creation and updates require district edit permission.",
                theme = theme,
            )
        }

        loadError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        formError?.let { StatusBanner(message = it, tone = BannerTone.Error, theme = theme) }
        formSuccess?.let { StatusBanner(message = it, tone = BannerTone.Success, theme = theme) }

        FieldRow {
            TextField(
                theme = theme,
                label = "School Name",
                value = draftName,
                placeholder = "Roosevelt High School",
                onValueChange = { draftName = it },
            )
            TextField(
                theme = theme,
                label = "Short Name",
                value = draftShortName,
                placeholder = "Roosevelt",
                onValueChange = { draftShortName = it },
            )
        }

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            ToggleChip(
                label = if (draftActive) "Active School" else "Inactive School",
                isSelected = draftActive,
                onClick = { if (canEdit) draftActive = !draftActive },
                theme = theme,
            )
            ActionButton(
                label = if (isSubmitting) "Saving..." else "Create School",
                onClick = {
                    val selectedDistrict = district
                    if (!canEdit || selectedDistrict == null) return@ActionButton
                    when {
                        draftName.isBlank() -> formError = "School name is required."
                        else -> scope.launch {
                            isSubmitting = true
                            formError = null
                            formSuccess = null
                            try {
                                apiClient.createSchool(
                                    CreateSchoolRequest(
                                        districtId = selectedDistrict.id,
                                        name = draftName.trim(),
                                        shortName = draftShortName.trim().ifBlank { null },
                                        active = draftActive,
                                    )
                                )
                                draftName = ""
                                draftShortName = ""
                                draftActive = true
                                refreshSchools(selectedDistrict)
                                onSchoolSaved()
                                formSuccess = "School created."
                            } catch (exception: Exception) {
                                formError = exception.message ?: "Unable to create school."
                            } finally {
                                isSubmitting = false
                            }
                        }
                    }
                },
                theme = theme,
                isDisabled = isSubmitting || !canEdit,
            )
        }

        if (schools.isEmpty() && !isRefreshing) {
            StatusBanner(
                message = "No schools are configured for ${district.name} yet.",
                theme = theme,
            )
        }

        schools.forEach { school ->
            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(12.px)
                        padding(18.px)
                        borderRadius(24.px)
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            property("justify-content", "space-between")
                            property("align-items", "start")
                            property("gap", "12px")
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    Div {
                        P({ style { margin(0.px); property("font-size", "1rem"); property("font-weight", "700") } }) {
                            Text(school.name)
                        }
                        P({ style { margin(0.px); property("color", theme.onSurfaceVariant) } }) {
                            Text(
                                listOfNotNull(
                                    school.shortName?.takeIf(String::isNotBlank)?.let { "Short name: $it" },
                                    if (school.active) "Active" else "Inactive",
                                ).joinToString(" • ")
                            )
                        }
                    }
                    ActionButton(
                        label = if (editDraft?.schoolId == school.id) "Cancel" else "Edit",
                        onClick = {
                            editDraft = if (editDraft?.schoolId == school.id) {
                                null
                            } else {
                                EditSchoolDraft(
                                    schoolId = school.id,
                                    name = school.name,
                                    shortName = school.shortName.orEmpty(),
                                    active = school.active,
                                )
                            }
                        },
                        theme = theme,
                        isPrimary = false,
                        isDisabled = !canEdit,
                    )
                }

                if (editDraft?.schoolId == school.id) {
                    FieldRow {
                        TextField(
                            theme = theme,
                            label = "School Name",
                            value = editDraft?.name.orEmpty(),
                            onValueChange = { value ->
                                editDraft = editDraft?.copy(name = value)
                            },
                        )
                        TextField(
                            theme = theme,
                            label = "Short Name",
                            value = editDraft?.shortName.orEmpty(),
                            onValueChange = { value ->
                                editDraft = editDraft?.copy(shortName = value)
                            },
                        )
                    }
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                gap(10.px)
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        ToggleChip(
                            label = if (editDraft?.active == true) "Active School" else "Inactive School",
                            isSelected = editDraft?.active == true,
                            onClick = {
                                editDraft = editDraft?.copy(active = editDraft?.active != true)
                            },
                            theme = theme,
                        )
                        ActionButton(
                            label = if (isSubmitting) "Saving..." else "Save School",
                            onClick = {
                                val activeEditDraft = editDraft ?: return@ActionButton
                                if (!canEdit) return@ActionButton
                                if (activeEditDraft.name.isBlank()) {
                                    formError = "School name is required."
                                    return@ActionButton
                                }
                                scope.launch {
                                    isSubmitting = true
                                    formError = null
                                    formSuccess = null
                                    try {
                                        apiClient.updateSchool(
                                            schoolId = activeEditDraft.schoolId,
                                            request = UpdateSchoolRequest(
                                                districtId = district.id,
                                                name = activeEditDraft.name.trim(),
                                                shortName = activeEditDraft.shortName.trim().ifBlank { null },
                                                active = activeEditDraft.active,
                                            )
                                        )
                                        editDraft = null
                                        refreshSchools(district)
                                        onSchoolSaved()
                                        formSuccess = "School updated."
                                    } catch (exception: Exception) {
                                        formError = exception.message ?: "Unable to update school."
                                    } finally {
                                        isSubmitting = false
                                    }
                                }
                            },
                            theme = theme,
                            isDisabled = isSubmitting || !canEdit,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ToggleChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    theme: MaterialThemePalette,
) {
    Button(
        attrs = {
            onClick { onClick() }
            style {
                padding(10.px, 14.px)
                borderRadius(999.px)
                property(
                    "background",
                    if (isSelected) theme.primaryContainer else theme.surfaceContainerHighest
                )
                property("color", if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant)
                property("border", "1px solid ${theme.outlineVariant}")
                property("font-weight", "700")
                property("cursor", "pointer")
            }
        }
    ) {
        Text(label)
    }
}
